/**
 * The header's money: `$1,240.55 · 99.20 reserved`.
 *
 * Two figures, never one summed number — `<Header>`'s own comment has said so
 * since Phase 3, and this is the component it was waiting for. The chip shows
 * **the wallet's USDU** (`wallet_balance`) — what the user holds — and falls
 * back to `available` until the venue has observed the wallet. It showed
 * `available` until then, which is the smaller of balance and allowance: a
 * wallet that had wrapped more than it approved read as holding only the
 * approved part. What the next order can spend is the ticket's to say, and it
 * does — with `ENABLE TRADING` when an approval is short. `order_locked` sits
 * beside it **only when it is non-zero**, labelled *reserved* (§F26l), because
 * a header that silently netted the two would make an order refuse for a
 * balance the header says the user has.
 *
 * **Below 1120px the two figures stack** rather than one of them going. Side by
 * side they cost ~110px of a header that has none to spare there, and hiding
 * `reserved` would bring back the netting problem above on exactly the devices
 * with the least room to explain it. Two lines of `body` over `caption` fit the
 * 48px header.
 *
 * It subscribes the `balance` store itself. `<Header>` renders on every route,
 * so a subscription hoisted into it would re-render the whole shell on every
 * balance frame — invariant 2, one level above where it usually bites.
 */

import { memo } from 'react'
import { Link } from 'react-router-dom'
import { NumCell } from '@/components/primitives'
import { useBalance } from '@/app/use-runtime'
import { balanceView, tradingBalanceOf } from '@/domain/balance'
import { formatUsdc } from '@/lib/format'
import { cx } from '@/lib/cx'

export const BalanceChip = memo(function BalanceChip() {
  const balance = useBalance<Record<string, unknown>>()
  if (!balance) return null

  const view = balanceView(balance)
  const available = view.rows.find((r) => r.key === 'available')
  const reserved = view.rows.find((r) => r.key === 'order_locked')
  if (!available) return null
  const wallet = typeof balance['wallet_balance'] === 'string'
  const shown = tradingBalanceOf(balance) ?? available

  return (
    <Link
      to="/portfolio"
      className={cx(
        'flex min-h-[var(--ud-tap-min)] shrink-0 flex-col items-end justify-center leading-tight text-body',
        'focus-visible:shadow-focus focus-visible:outline-none',
        // components.md §1's hit-target floor below 768px; side by side from `lg`.
        'md:min-h-0 lg:flex-row lg:items-baseline lg:gap-1_5 lg:leading-normal',
      )}
      title={wallet ? 'Trading balance' : 'Balance'}
    >
      <NumCell
        value={formatUsdc(shown.value, { dp: 2 })}
        compare={shown.micro}
        align="left"
        className="text-text-primary"
      />
      {reserved && reserved.micro > 0n ? (
        <span className="flex items-baseline gap-1 text-caption text-text-tertiary">
          <span aria-hidden="true">·</span>
          <NumCell
            value={formatUsdc(reserved.value, { dp: 2 })}
            compare={reserved.micro}
            align="left"
            className="text-caption text-text-tertiary"
          />
          reserved
        </span>
      ) : null}
    </Link>
  )
})
